'use client';

import { ComponentProps } from 'react';
import { TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@/primitives';
import LabelSelectInput from '../../formControls/LabelSelectInput';
import { FILTER_INPUT_TYPE } from '../constants';

interface Props {
  disabled: boolean;
  label: string;
  options?: ComponentProps<typeof LabelSelectInput>['options'];
  type:
    | ComponentProps<typeof TextField>['type']
    | typeof FILTER_INPUT_TYPE.DROPDOWN;
  value: string;
  multiple?: boolean;
  onChange: (value: string) => void;
  selectSx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
}

export default function FilterConditionInput({
  disabled,
  label,
  options = [],
  type,
  value,
  multiple = false,
  onChange,
  selectSx,
  textFieldSx,
}: Props) {
  if (type === FILTER_INPUT_TYPE.DROPDOWN && multiple) {
    const selectedValues = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    const hasValue = selectedValues.length > 0;
    const renderValue = (selected: unknown) => {
      if (!selected || (Array.isArray(selected) && selected.length === 0)) {
        return (
          <Typography color="textSecondary" variant="labellarge">
            {label || 'Value'}
          </Typography>
        );
      }
      const selectedArray = Array.isArray(selected)
        ? selected
        : String(selected)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
      const labels = selectedArray
        .map(
          (selectedValue) =>
            options.find((op) => op.value === selectedValue)?.label ??
            selectedValue,
        )
        .filter(Boolean);
      return (
        <Typography variant="labellarge">
          {labels.length > 2 ? `${labels.length} selected` : labels.join(', ')}
        </Typography>
      );
    };
    return (
      <FormControl
        variant="standard"
        disabled={disabled}
        sx={{ minWidth: 132, width: '100%' }}
      >
        {label && <InputLabel shrink={hasValue}>{label}</InputLabel>}
        <Select
          disableUnderline
          multiple
          value={selectedValues}
          onChange={(e) => {
            const nextValue = e.target.value;
            const selected =
              typeof nextValue === 'string'
                ? nextValue.split(',')
                : (nextValue as string[]);
            onChange(selected.filter(Boolean).join(','));
          }}
          renderValue={renderValue}
          sx={{ paddingLeft: '0.5rem', width: '100%', ...selectSx }}
        >
          {options.map(({ key, label: optionLabel, value: optionValue }) => (
            <MenuItem key={key ?? String(optionValue)} value={optionValue}>
              <Checkbox
                size="small"
                checked={selectedValues.includes(String(optionValue))}
                sx={{ mr: 1 }}
              />
              <Typography
                variant="labellarge"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {optionLabel}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (type === FILTER_INPUT_TYPE.DROPDOWN) {
    return (
      <LabelSelectInput
        disabled={disabled}
        label={label}
        labelProps={{
          shrink: true,
        }}
        options={options}
        selectProps={{
          onChange: (e) => {
            onChange(String(e.target.value));
          },
          sx: { ...selectSx, width: '100%' },
        }}
        selectedValue={value}
        sx={{ minWidth: 132, width: '100%' }}
      />
    );
  }

  return (
    <TextField
      color="info"
      disabled={disabled}
      fullWidth
      label={label || undefined}
      placeholder={!label ? 'Value' : undefined}
      sx={{ minWidth: 132, width: '100%', ...textFieldSx }}
      type={type}
      value={value}
      onChange={(e) => {
        onChange(String(e.target.value));
      }}
      slotProps={{
        inputLabel: {
          shrink: type === FILTER_INPUT_TYPE.DATE || !!value,
        },
      }}
    />
  );
}
